import { formatMoney, money, parseMoney, toDecimalString } from "@stb/core/money"

/**
 * Display and input helpers for the invoicing UI. Amounts are integer minor units, never floats;
 * the only decimal handling is string parsing through `@stb/core/money`. No server imports.
 */

/** `466000` -> `A$4,660.00` (the logged-in app's money style; the paper document uses en-AU). */
export function formatAud(minor: number): string {
  return formatMoney(money(minor, "AUD"), "en-US")
}

const dayFormat = new Intl.DateTimeFormat("en-AU", {
  weekday: "short",
  day: "numeric",
  month: "short",
  timeZone: "UTC",
})

/** `2026-10-05` -> `Mon 5 Oct`. Date-only values are read as UTC so the day never shifts. */
export function formatDay(date: string): string {
  return dayFormat.format(new Date(`${date}T00:00:00Z`)).replace(",", "")
}

const DAY_MS = 86_400_000

/** Whole days from `due` to `today` (both `YYYY-MM-DD`); 0 when not past due. */
export function daysOverdue(due: string, today: string): number {
  const days = Math.round(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / DAY_MS,
  )
  return Math.max(0, days)
}

/** Whole days from `today` to `due`; 0 when due today or earlier. */
export function daysUntil(due: string, today: string): number {
  return Math.max(
    0,
    Math.round((Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY_MS),
  )
}

/** `"1,234.50"` -> `123450`; `null` for anything that is not a plain non-negative amount. */
export function parseAmountMinor(input: string): number | null {
  try {
    const parsed = parseMoney(input, "AUD").amount
    return parsed < 0 ? null : parsed
  } catch {
    return null
  }
}

const QUANTITY_PATTERN = /^(\d+)(?:\.(\d{1,3}))?$/

/** `"1.5"` -> `1500` thousandths; `null` for zero, negatives, junk or more than 3 decimals. */
export function parseQuantityMilli(input: string): number | null {
  const match = QUANTITY_PATTERN.exec(input.trim())
  if (!match) return null
  const milli = Number(match[1]) * 1000 + Number((match[2] ?? "").padEnd(3, "0"))
  return milli > 0 && Number.isSafeInteger(milli) ? milli : null
}

/** `1500` -> `"1.5"`, `2000` -> `"2"`. */
export function formatQuantityInput(milli: number): string {
  const whole = Math.trunc(milli / 1000)
  const fraction = String(milli % 1000)
    .padStart(3, "0")
    .replace(/0+$/, "")
  return fraction ? `${whole}.${fraction}` : String(whole)
}

/** `12345` -> `"123.45"`, for filling an amount input. */
export function formatAmountInput(minor: number): string {
  return toDecimalString(money(minor, "AUD"))
}
